import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { reject, join, filter, findIndex, remove } from 'ramda'
import { getPrimaryOther, getIsGroupChat, getChatAvatar } from '@/features/Chat/utils'
import useCurrentSession from '@/hooks/use-current-session'
import useRole from '@/hooks/use-role'
import formatName from '@/utils/format-name'
import hideUnit from '@/utils/hide-unit'
import isVerified from '@/utils/is-verified'
import { getImageUrl } from '@/utils/image'
import ChatCardBase from './chat-card-base'
import metadata from '../chat-room/chat-message/metadata'

const ChatCard = ({
  timeline: {
    applications,
    buildings,
    last_event,
    last_event_message,
    listings,
    is_read,
    timeline_room,
    state_machine,
    units,
    users,
    primary_user_ids,
    event_info,
    created_at,
    is_favourite,
    contracts
  },
  highlightObj
}) => {
  const { t } = useTranslation()
  const role = useRole()
  const contract = contracts || {}
  const me = useCurrentSession()
  const isThem = me?.id !== last_event_message?.user_id
  const isLandlord = useRole() === 'landlord'
  const customMessage = metadata(isThem, last_event_message, t, role)

  if (users.them.length <= 0 && users.us.length <= 0) return null

  const unitHidden = hideUnit(units, contract)

  const getLandlordAddress = () => {
    const unitNumber = units.unit_number
    const streetAddress = buildings.full_street_name
    const address = unitNumber ? `${unitNumber} - ${streetAddress}` : streetAddress

    return address
  }

  const getTenantHouseAddress = () => {
    const unitNumber = unitHidden ? t('common.house', 'House') : units.unit_number
    const streetAddress = unitHidden ? buildings.street_name : buildings.full_street_name
    const address = unitNumber ? `${unitNumber} - ${streetAddress}` : streetAddress

    return address
  }

  const getTenantApartmentAddress = () => {
    const unitNumber = unitHidden ? null : units.unit_number
    const streetAddress = buildings.full_street_name
    const address = unitNumber ? `${unitNumber} - ${streetAddress}` : streetAddress

    return address
  }

  const getTenantAddress = () => {
    const isHouse = buildings.building_type_txt_id === 'house'
    const address = isHouse ? getTenantHouseAddress() : getTenantApartmentAddress()

    return address
  }

  const parseSenderName = sender_name => {
    const hasBolded = x => x.includes('<strong>')
    const arr = sender_name.split(',')
    const indexOfUser = findIndex(x => x === me?.preferred_name)(arr)
    const membersArrayExcludingUser = remove(indexOfUser, 1, arr)
    const boldedMembers = filter(hasBolded, membersArrayExcludingUser)
    const nonBoldedMembers = reject(hasBolded, membersArrayExcludingUser)
    const parsedString = join(', ', [
      ...boldedMembers,
      ...nonBoldedMembers,
      t('b.chat.view.you.text', 'You')
    ])
    return parsedString
  }

  const primaryOther = getPrimaryOther(users, primary_user_ids)
  const isGroupChat = getIsGroupChat(users)
  const getHighlightedMessage = highlightObj => {
    if (highlightObj?.sender_name) {
      return parseSenderName(highlightObj.sender_name)
    } else if (highlightObj?.partial) {
      return highlightObj.partial
    } else if (highlightObj?.fullMatch) {
      return highlightObj?.fullMatch
    } else {
      return null
    }
  }
  const chatAvatar = isLandlord
    ? getImageUrl(primaryOther?.avatar?.aws_s3_key, {
        width: 80,
        height: 80
      })
    : getChatAvatar(users, primary_user_ids, 80)

  return (
    <ChatCardBase
      created_at={created_at}
      name={`${formatName(primaryOther)}${
        !isLandlord && isGroupChat ? ` +${users.them.length - 1}` : ''
      }`}
      address={role === 'tenant' ? getTenantAddress() : getLandlordAddress()}
      avatar={chatAvatar}
      last_event={last_event}
      isNameResult={!!highlightObj?.sender_name}
      highlight={getHighlightedMessage(highlightObj)}
      last_event_message={
        event_info?.message || customMessage?.title || last_event_message?.event_info.message
      }
      targetMessageId={last_event_message?.id}
      is_read={is_read}
      timeline_room={timeline_room}
      listingStateMachine={listings?.state_machine}
      stateMachine={state_machine}
      hasApplied={!!applications?.id}
      isIdVerified={isVerified(primaryOther, primaryOther?.company)}
      isGroupChat={isGroupChat}
      is_favourite={is_favourite}
    />
  )
}

ChatCard.defaultProps = {
  timeline: {},
  url: '',
  name: '',
  unit: '',
  last_event_read: '',
  last_event: '',
  is_read: ''
}

export default memo(ChatCard)
